import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { usePermissions } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loading } from '@/components/ui/loading'
import { AlertCircle, ArrowLeft, Building2, Check, LogIn, Save, Search, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Id } from '../../../convex/_generated/dataModel'

interface UserFormData {
  email: string
  nome: string
  cognome: string
  telefono: string
  ruolo: 'amministratore' | 'tecnico_lab' | 'clinic_owner' | 'dentista' | 'aso' | 'staff'
  company_id: string
  company_ids: string[]
  active: boolean
  can_view_customer_health: boolean
}

type UserFormErrors = Partial<
  Record<'email' | 'nome' | 'cognome' | 'telefono' | 'company_id', string>
>

const errorFieldIds: Record<keyof UserFormErrors, string> = {
  email: 'user-email',
  nome: 'user-first-name',
  cognome: 'user-last-name',
  telefono: 'user-phone',
  company_id: 'company-selection',
}

export default function UserForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  
  const { hasPermission } = usePermissions()
  const sessionInfo = useQuery(api.profiles.getSessionInfo)
  
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    nome: '',
    cognome: '',
    telefono: '',
    ruolo: 'dentista',
    company_id: '',
    company_ids: [],
    active: true,
    can_view_customer_health: false,
  })
  
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<UserFormErrors>({})
  const [companySearch, setCompanySearch] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const submitErrorRef = useRef<HTMLDivElement>(null)

  // Get user data if editing
  const user = useQuery(
    api.profiles.getProfile,
    isEdit ? { profileId: id as Id<"profiles"> } : "skip"
  )

  // Check permissions
  const canManageUsers = hasPermission('manage_users')
  
  // Get companies for dropdown conditionally
  const companies = useQuery(
    api.companies.listCompanies, 
    canManageUsers ? {} : "skip"
  )

  // Mutations
  const createUser = useMutation(api.profiles.createProfile)
  const updateUser = useMutation(api.profiles.updateProfile)

  // Load user data when editing
  useEffect(() => {
    if (isEdit && user) {
      const companyIds = Array.from(
        new Set(
          [...(user.company_ids ?? []), user.company_id]
            .filter(Boolean)
            .map(String)
        )
      )
      setFormData({
        email: user.email || '',
        nome: user.nome || '',
        cognome: user.cognome || '',
        telefono: user.telefono || '',
        ruolo: user.ruolo || 'dentista',
        company_id: user.company_id || companyIds[0] || '',
        company_ids: companyIds,
        active: user.active ?? true,
        can_view_customer_health:
          user.extra_permissions?.includes('view_customer_health') ?? false,
      })
    }
  }, [isEdit, user])

  if (!canManageUsers) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">Accesso negato</h2>
            <p className="text-gray-600">Non hai i permessi per gestire gli utenti.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if ((isEdit && user === undefined) || companies === undefined || sessionInfo === undefined) {
    return <Loading />
  }

  const isDirectAdmin =
    sessionInfo?.ruolo === 'amministratore' &&
    !sessionInfo.impersonating_profile_id
  const isPendingAuth0 = Boolean(isEdit && user && !user.user_id)
  const canEditEmail = !isEdit || isPendingAuth0

  const handleInputChange = (field: keyof UserFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setSubmitError(null)
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const toggleCompany = (companyId: string) => {
    setFormData((previous) => {
      const selected = previous.company_ids.includes(companyId)
        ? previous.company_ids.filter((id) => id !== companyId)
        : [...previous.company_ids, companyId]
      const primaryCompanyId = selected.includes(previous.company_id)
        ? previous.company_id
        : selected[0] ?? ''
      return {
        ...previous,
        company_ids: selected,
        company_id: primaryCompanyId,
      }
    })
    if (errors.company_id) {
      setErrors((previous) => ({ ...previous, company_id: undefined }))
    }
  }

  const validateForm = (): UserFormErrors => {
    const newErrors: UserFormErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = 'Email è obbligatoria'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida'
    }

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome è obbligatorio'
    }

    if (!formData.cognome.trim()) {
      newErrors.cognome = 'Cognome è obbligatorio'
    }

    if (formData.telefono && !/^[\+]?[0-9\s\-\(\)]{8,}$/.test(formData.telefono)) {
      newErrors.telefono = 'Numero di telefono non valido'
    }

    // Validate company requirement for non-lab users
    if (
      formData.ruolo !== 'amministratore' &&
      formData.ruolo !== 'tecnico_lab' &&
      formData.company_ids.length === 0
    ) {
      newErrors.company_id = 'Seleziona almeno una clinica collegata'
    }

    setErrors(newErrors)
    return newErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationErrors = validateForm()
    const firstError = Object.keys(validationErrors)[0] as keyof UserFormErrors | undefined
    if (firstError) {
      requestAnimationFrame(() => {
        const target = document.getElementById(errorFieldIds[firstError])
        target?.focus()
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      return
    }

    setLoading(true)
    
    try {
      if (isEdit) {
        // Update existing user
        const updateData = {
          profileId: id as Id<'profiles'>,
          ...(canEditEmail ? { email: formData.email.trim().toLowerCase() } : {}),
          nome: formData.nome.trim(),
          cognome: formData.cognome.trim(),
          telefono: formData.telefono.trim() || undefined,
          ruolo: formData.ruolo,
          company_id: formData.company_id || undefined,
          company_ids: formData.company_ids as Id<'companies'>[],
          active: formData.active,
          ...(isDirectAdmin
            ? {
                extra_permissions:
                  formData.ruolo === 'clinic_owner' &&
                  formData.can_view_customer_health
                    ? ['view_customer_health' as const]
                    : [],
              }
            : {}),
        }

        await updateUser(updateData)
      } else {
        await createUser({
          email: formData.email.trim().toLowerCase(),
          nome: formData.nome.trim(),
          cognome: formData.cognome.trim(),
          telefono: formData.telefono.trim() || undefined,
          ruolo: formData.ruolo,
          company_id: formData.company_id
            ? formData.company_id as Id<'companies'>
            : undefined,
          company_ids: formData.company_ids as Id<'companies'>[],
          active: formData.active,
          ...(isDirectAdmin
            ? {
                extra_permissions:
                  formData.ruolo === 'clinic_owner' &&
                  formData.can_view_customer_health
                    ? ['view_customer_health' as const]
                    : [],
              }
            : {}),
        })
      }

      navigate('/users')
    } catch (error) {
      console.error('Errore salvataggio utente:', error)
      const rawMessage = error instanceof Error ? error.message : ''
      const knownMessage = [
        'Esiste già un profilo con questa email',
        'Email non valida',
        'Non puoi collegare l’utente a una società non autorizzata',
        'Solo un amministratore può assegnare ruoli lato laboratorio',
        'Solo un amministratore può assegnare permessi individuali',
      ].find((message) => rawMessage.includes(message))
      setSubmitError(
        knownMessage?.replace('società', 'clinica') ?? 'Non è stato possibile salvare l’utente. Controlla i dati e riprova.'
      )
      requestAnimationFrame(() => {
        submitErrorRef.current?.focus()
        submitErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    } finally {
      setLoading(false)
    }
  }

  const clinicalRoles = [
    { value: 'clinic_owner', label: 'Titolare clinica' },
    { value: 'dentista', label: 'Dentista' },
    { value: 'aso', label: 'ASO' },
    { value: 'staff', label: 'Personale clinica' }
  ]
  const availableRoles = isDirectAdmin
    ? [
        { value: 'amministratore', label: 'Amministratore' },
        { value: 'tecnico_lab', label: 'Tecnico laboratorio' },
        ...clinicalRoles,
      ]
    : clinicalRoles
  const companyRows = [...(Array.isArray(companies) ? companies : (companies?.page ?? []))]
    .sort((a, b) => a.nome.localeCompare(b.nome, 'it'))
  const filteredCompanies = companyRows.filter((company) =>
    company.nome.toLocaleLowerCase('it-IT').includes(
      companySearch.trim().toLocaleLowerCase('it-IT')
    )
  ) ?? []

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <Button asChild variant="outline" size="sm">
          <Link to="/users" aria-label="Torna alla gestione utenti">
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold flex items-center space-x-2">
            <Users className="w-8 h-8" />
            <span>{isEdit ? 'Modifica utente' : 'Nuovo utente'}</span>
          </h1>
          <p className="text-gray-600 mt-2">
            {isEdit
              ? 'Aggiorna ruolo, cliniche e dati del profilo.'
              : 'Prepara il profilo che verrà collegato ad Auth0 al primo accesso.'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Dati utente</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {submitError && (
              <div
                ref={submitErrorRef}
                id="user-submit-error"
                tabIndex={-1}
                role="alert"
                className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-950 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold">Utente non salvato</p>
                  <p className="mt-0.5 text-sm leading-5">{submitError}</p>
                </div>
              </div>
            )}

            {(!isEdit || isPendingAuth0) && (
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-950">
                <LogIn className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold">
                    {isPendingAuth0 ? 'In attesa del primo accesso' : 'Collegamento Auth0 automatico'}
                  </p>
                  <p className="mt-0.5 text-sm leading-5 text-blue-800">
                    Non serve impostare una password. Al primo accesso Auth0 collegherà questo profilo usando la stessa email verificata.
                  </p>
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="user-email" className="block text-sm font-medium mb-2">
                Email *
              </label>
              <Input
                id="user-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="utente@email.com"
                required
                maxLength={254}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'user-email-error' : isEdit ? 'user-email-help' : undefined}
                readOnly={!canEditEmail}
                className={`${errors.email ? 'border-red-500' : ''} ${!canEditEmail ? 'bg-gray-50 text-gray-600' : ''}`}
              />
              {errors.email && (
                <p id="user-email-error" className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
              {isEdit && (
                <p id="user-email-help" className="text-xs text-gray-500 mt-1">
                  {isPendingAuth0
                    ? 'Puoi correggerla finché il profilo non viene collegato ad Auth0.'
                    : 'Email già collegata ad Auth0 e non modificabile.'}
                </p>
              )}
            </div>

            {/* Nome e Cognome */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="user-first-name" className="block text-sm font-medium mb-2">
                  Nome *
                </label>
                <Input
                  id="user-first-name"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder="Mario"
                  required
                  maxLength={80}
                  className={errors.nome ? 'border-red-500' : ''}
                  aria-invalid={Boolean(errors.nome)}
                  aria-describedby={errors.nome ? 'user-first-name-error' : undefined}
                />
                {errors.nome && (
                  <p id="user-first-name-error" className="text-red-500 text-sm mt-1">{errors.nome}</p>
                )}
              </div>
              <div>
                <label htmlFor="user-last-name" className="block text-sm font-medium mb-2">
                  Cognome *
                </label>
                <Input
                  id="user-last-name"
                  value={formData.cognome}
                  onChange={(e) => handleInputChange('cognome', e.target.value)}
                  placeholder="Rossi"
                  required
                  maxLength={80}
                  className={errors.cognome ? 'border-red-500' : ''}
                  aria-invalid={Boolean(errors.cognome)}
                  aria-describedby={errors.cognome ? 'user-last-name-error' : undefined}
                />
                {errors.cognome && (
                  <p id="user-last-name-error" className="text-red-500 text-sm mt-1">{errors.cognome}</p>
                )}
              </div>
            </div>

            {/* Telefono */}
            <div>
              <label htmlFor="user-phone" className="block text-sm font-medium mb-2">
                Telefono
              </label>
              <Input
                id="user-phone"
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                placeholder="+39 123 456 7890"
                maxLength={40}
                className={errors.telefono ? 'border-red-500' : ''}
                aria-invalid={Boolean(errors.telefono)}
                aria-describedby={errors.telefono ? 'user-phone-error' : undefined}
              />
              {errors.telefono && (
                <p id="user-phone-error" className="text-red-500 text-sm mt-1">{errors.telefono}</p>
              )}
            </div>

            {/* Password - Removed for simplified flow */}

            {/* Ruolo */}
            <div>
              <label htmlFor="user-role" className="block text-sm font-medium mb-2">
                Ruolo *
              </label>
              <select
                id="user-role"
                value={formData.ruolo}
                onChange={(e) => {
                  const role = e.target.value as UserFormData['ruolo']
                  setFormData((previous) => ({
                    ...previous,
                    ruolo: role,
                    ...(role === 'amministratore' || role === 'tecnico_lab'
                      ? { company_id: '', company_ids: [] }
                      : {}),
                  }))
                  setSubmitError(null)
                  setErrors((previous) => ({ ...previous, company_id: undefined }))
                }}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Società collegate */}
            {formData.ruolo === 'amministratore' || formData.ruolo === 'tecnico_lab' ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Accesso lato laboratorio</p>
                    <p className="mt-1 text-sm leading-5 text-gray-600">
                      Amministratori e tecnici lavorano trasversalmente e non richiedono cliniche collegate.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <fieldset
                id="company-selection"
                tabIndex={-1}
                aria-invalid={Boolean(errors.company_id)}
                aria-describedby={errors.company_id ? 'company-selection-error' : 'company-selection-help'}
                className="focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <legend className="text-sm font-medium text-gray-900">Cliniche collegate *</legend>
                <p id="company-selection-help" className="mt-1 text-sm text-gray-500">
                  L’utente potrà cambiare clinica attiva dall’intestazione dell’applicazione.
                </p>
                <div className="relative mt-3">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <Input
                    value={companySearch}
                    onChange={(event) => setCompanySearch(event.target.value)}
                    placeholder="Cerca clinica..."
                    aria-label="Cerca tra le cliniche"
                    className="pl-9"
                  />
                </div>
                <div
                  className={`mt-2 max-h-64 overflow-y-auto rounded-lg border bg-white ${
                    errors.company_id ? 'border-red-400' : 'border-gray-200'
                  }`}
                >
                  {filteredCompanies.map((company, index) => {
                    const selected = formData.company_ids.includes(company._id)
                    return (
                      <label
                        key={company._id}
                        className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50 ${
                          index > 0 ? 'border-t border-gray-100' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleCompany(company._id)}
                          className="peer sr-only"
                        />
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 ${
                            selected
                              ? 'border-secondary bg-secondary text-white'
                              : 'border-gray-300 bg-white text-transparent'
                          }`}
                          aria-hidden="true"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">
                          {company.nome}
                        </span>
                        {formData.company_id === company._id && selected && (
                          <span className="rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-medium text-secondary">
                            Principale
                          </span>
                        )}
                      </label>
                    )
                  })}
                  {filteredCompanies.length === 0 && (
                    <p className="px-4 py-6 text-center text-sm text-gray-500">
                      Nessuna clinica trovata.
                    </p>
                  )}
                </div>
                {errors.company_id && (
                  <p id="company-selection-error" className="mt-2 text-sm text-red-600" role="alert">
                    {errors.company_id}
                  </p>
                )}

                {formData.company_ids.length > 1 && (
                  <div className="mt-4">
                    <label htmlFor="primary-company" className="block text-sm font-medium text-gray-800">
                      Clinica principale
                    </label>
                    <select
                      id="primary-company"
                      value={formData.company_id}
                      onChange={(event) => handleInputChange('company_id', event.target.value)}
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
                    >
                      {formData.company_ids.map((companyId) => {
                        const company = companyRows.find((item) => item._id === companyId)
                        return company ? (
                          <option key={company._id} value={company._id}>{company.nome}</option>
                        ) : null
                      })}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Usata come contesto iniziale quando non è stata ancora scelta una clinica attiva.
                    </p>
                  </div>
                )}
              </fieldset>
            )}

            {/* Stato */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Utente attivo</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Gli utenti inattivi non possono accedere al sistema
              </p>
            </div>

            {isDirectAdmin && formData.ruolo === 'clinic_owner' && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.can_view_customer_health}
                    onChange={(e) =>
                      handleInputChange('can_view_customer_health', e.target.checked)
                    }
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      Performance
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-gray-500">
                      Consente di consultare volumi, feedback e indicatori economici autorizzati.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse gap-3 border-t pt-6 sm:flex-row sm:justify-end">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/users">
                  Annulla
                </Link>
              </Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto">
                <Save className="w-4 h-4 mr-2" />
                {loading
                  ? isEdit ? 'Salvataggio…' : 'Creazione…'
                  : isEdit ? 'Aggiorna utente' : 'Crea utente'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
